import { useMemo, useState } from 'react';
import { Modal, Pagination } from 'antd';
import GenericMap from '../map/GenericMap';
import { MapMarker } from '../map/GenericMap';
import { useTranslation } from 'react-i18next';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';
import LocationCard from '../locationCard/LocationCard';
import { formatPrice } from '../../utils/formatPrice';
import dayjs from 'dayjs';
import { useMediaQuery } from 'react-responsive';
import RESOURCE_CONFIGS from '../../constants/resourceConfig';
import { sanitizeInput } from '../../utils/sanitizeInput';
import { PriceStatusEnum } from '../../enums/PriceStatusEnum';

interface TaskLocationModalProps {
  visible: boolean;
  onClose: () => void;
  inspection: {
    doneBy: { name: string; id: string };
    date: string;
    coordinates: { lat: number; long: number };
    prices: Array<{ price: string; product: string; status?: string }>;
  } | null;
}
interface MapPopupContentProps {
  data: {
    products: Array<{ price: string; product: string; status?: string }>;
    reportedBy: string;
    reportedDate: string;
  };
  t: any;
  formatDate: (timestamp: string) => string;
}

const MapPopupContent = ({ data, t, formatDate }: MapPopupContentProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 5;

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedProducts = data?.products?.slice(startIndex, startIndex + pageSize) || [];

  return (
    <div className="max-w-[260px] p-1 text-xs sm:max-w-xs sm:text-sm">
      <h3 className="mb-2 text-sm font-semibold sm:text-base">{t('inspectionDetails')}</h3>
      <div className="space-y-1">
        {paginatedProducts.map((product, idx) => (
          <div key={idx} className="flex items-center justify-between gap-2 py-1">
            <span className="break-words font-medium text-gray-800">{sanitizeInput(product?.product)}</span>
            <span className="shrink-0 font-bold text-green-600">
              {product?.status === PriceStatusEnum.PRODUCTNOTFOUND ? (
                <span className="font-semibold text-red-500">{t('price:priceNotFound')}</span>
              ) : (
                formatPrice(product?.price)
              )}
            </span>
          </div>
        ))}
      </div>

      {data?.products && data.products.length > 0 && (
        <div className="my-2 flex justify-center overflow-x-auto py-1">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={data.products.length}
            onChange={(page) => setCurrentPage(page)}
            size="small"
            showSizeChanger={false}
            responsive
          />
        </div>
      )}

      <div className="mt-2 space-y-1 text-xs text-gray-600">
        <p>
          <span className="font-medium text-gray-700">{t('reportedBy')}:</span> {data?.reportedBy}
        </p>
        <p>
          <span className="font-medium text-gray-700">{t('date')}:</span> {formatDate(data?.reportedDate)}
        </p>
      </div>
    </div>
  );
};

const TaskLocationModal = ({ visible, onClose, inspection }: TaskLocationModalProps) => {
  const { t } = useTranslation(['task', 'price']);
  const { isOnline } = useIsOnlineContext();

  const isMobile = useMediaQuery({
    query: `(max-width: ${RESOURCE_CONFIGS.RESPONSIVE_BREAKPOINTS.SM})`,
  });

  const formatDate = (timestamp: string) => {
    const numTimestamp = parseInt(timestamp);
    if (isNaN(numTimestamp)) {
      return 'Invalid Date';
    }
    try {
      return new Date(numTimestamp).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const markers: MapMarker[] = useMemo(() => {
    const allMarkers: MapMarker[] = [];

    if (visible && inspection && inspection.coordinates) {
      const coordinates = {
        lat: inspection.coordinates.lat,
        long: inspection.coordinates.long,
      };

      allMarkers.push({
        id: 'inspection_location',
        coordinates: coordinates,
        data: {
          products: inspection.prices,
          reportedBy: inspection.doneBy.name,
          reportedDate: inspection.date,
        },
        iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
        iconSize: [25, 25],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12],
      });
    }

    return allMarkers;
  }, [inspection, visible]);

  const renderPopup = (marker: MapMarker) => {
    const data = marker.data as {
      products: Array<{ price: string; product: string }>;
      reportedBy: string;
      reportedDate: string;
    };

    return <MapPopupContent data={data} t={t} formatDate={formatDate} />;
  };

  const getMapCenter = (): [number, number] => {
    if (markers.length === 0) {
      return [14.1, -87.2];
    }

    const avgLat =
      markers.reduce((sum, marker) => sum + marker.coordinates.lat, 0) / markers.length;
    const avgLong =
      markers.reduce((sum, marker) => sum + marker.coordinates.long, 0) / markers.length;

    return [avgLat, avgLong];
  };

  return (
    <Modal
      title={t('priceLocationDetails')}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '94vw' : 800}
      centered
      style={{ top: isMobile ? 8 : 20, margin: '0 auto', maxWidth: '100vw' }}
      styles={{
        body: {
          maxHeight: isMobile ? 'calc(90vh - 40px)' : 'calc(85vh - 60px)',
          overflowY: 'auto',
          paddingRight: isMobile ? 0 : 4,
        },
      }}
      className="task-location-modal"
    >
      <div className="mb-3">
        <div className="grid grid-cols-1 gap-2 text-xs sm:text-sm md:grid-cols-2">
          <div>
            <span className="font-semibold">{t('reportedBy')}:</span>{' '}
            {inspection?.doneBy?.name || 'N/A'}
          </div>
          <div>
            <span className="font-semibold">{t('date')}:</span>{' '}
            {inspection ? dayjs(Number(inspection.date)).format('YYYY-MM-DD') : 'N/A'}
          </div>
          <div>
            <span className="font-semibold">{t('products')}:</span>{' '}
            {inspection?.prices?.length || 0}
          </div>
          <div>
            <span className="font-semibold">{t('coordinates')}:</span>{' '}
            {inspection?.coordinates &&
            inspection?.coordinates?.lat &&
            inspection?.coordinates?.long
              ? `${inspection?.coordinates?.lat.toFixed(6)}, ${inspection?.coordinates?.long.toFixed(6)}`
              : 'N/A'}
          </div>
        </div>
      </div>

      {isOnline &&
      inspection?.coordinates &&
      inspection?.coordinates?.lat &&
      inspection?.coordinates?.long ? (
        <div className="h-52 w-full sm:h-80 md:h-96">
          <GenericMap
            markers={markers}
            center={getMapCenter()}
            zoom={markers.length === 1 ? 15 : 12}
            height="100%"
            loading={false}
            renderPopup={renderPopup}
            onMarkerClick={(marker) => {}}
          />
        </div>
      ) : isOnline ? (
        <LocationCard scenario="nocoordinates" inspection={inspection} />
      ) : (
        <></>
      )}
      {!isOnline && <LocationCard scenario="offline" inspection={inspection} />}

      <div className="mt-3 text-xs text-gray-600 sm:text-sm">{t('accuracyOfLocation')}</div>
    </Modal>
  );
};

export default TaskLocationModal;
